import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Globe, Lightbulb, Mic, Paperclip, Send } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { TemporaryChatContext } from '../../Context/temporaryChatContext';
import { useChatSubmit } from '../../hooks/useChatSubmit';
import agentService from '../../services/agentService';
import teamService from '../../services/teamService';
import UploadFileModal from '../UploadFileModal';
import Loaders from '../Loaders';
import { DEFAULT_WALT_AGENT, DEFAULT_WALT_ID } from './waltDefaults';
import {
  ComposerRow,
  ComposerField,
  ComposerTools,
  ComposerSend,
} from '../SearchBlock/ChatComposer.styles';

function resolveAgentIcon(agent) {
  if (!agent?.icon) return null;
  if (agent.icon.startsWith('http://') || agent.icon.startsWith('https://') || agent.icon.startsWith('data:')) {
    return agent.icon.split('?')[0].toLowerCase().endsWith('.svg') ? null : agent.icon;
  }
  return `data:image/png;base64,${agent.icon}`;
}

/**
 * FeedaLabs hybrid chat composer (light): pill field + agent/G chips + tools + external send.
 * Behavior mirrors platformtl PolyChatInput (session gate + New Chat hint).
 */
export default function WaltChatInput({ sessionId, createSession = false }) {
  const navigate = useNavigate();
  const [input, setInput] = useState('');
  const [showHint, setShowHint] = useState(false);
  const [autoOpen, setAutoOpen] = useState(false);
  const [webOpen, setWebOpen] = useState(false);
  const [attachOpen, setAttachOpen] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const textareaRef = useRef(null);
  const autoRef = useRef(null);
  const webRef = useRef(null);
  const recognitionRef = useRef(null);

  const {
    selectedAgent,
    sessionAgent,
    updateAgent,
    setSessionAgent,
    setChatmessages,
    setSessionID,
    webSearch,
    updateWebSearch,
  } = useContext(TemporaryChatContext);

  const { submitMessage, isSubmitting, loading } = useChatSubmit({ sessionId, createSession });
  const hasSession = Boolean(sessionId) || createSession;
  const isDisabled = !hasSession || isSubmitting;

  const agentsQuery = useQuery({
    queryKey: ['get-all-agents', 'v2-no-domain'],
    queryFn: () => agentService.getAllAgent(),
    staleTime: 5 * 60 * 1000,
  });
  const { _data: teamsPayload } = teamService.GetTeamList(null, fetch);
  const teamData = teamsPayload?.teams || [];

  const agentOptions = useMemo(() => {
    const agentList = agentsQuery.data?.agents || [];
    const hasWalt = agentList.some(
      a => String(a.id) === DEFAULT_WALT_ID || (a.agent_name || '').toLowerCase() === 'walt',
    );
    const list = [
      ...(hasWalt
        ? []
        : [{ id: DEFAULT_WALT_AGENT.value, agent_name: DEFAULT_WALT_AGENT.label, icon: DEFAULT_WALT_AGENT.icon }]),
      ...agentList,
    ];
    return list.sort((a, b) => {
      const aWalt = String(a.id) === DEFAULT_WALT_ID || (a.agent_name || '').toLowerCase() === 'walt';
      const bWalt = String(b.id) === DEFAULT_WALT_ID || (b.agent_name || '').toLowerCase() === 'walt';
      if (aWalt && !bWalt) return -1;
      if (!aWalt && bWalt) return 1;
      return 0;
    });
  }, [agentsQuery.data?.agents]);

  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) return undefined;

    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'en-US';
    recognition.onresult = event => {
      let transcript = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        transcript += event.results[i][0].transcript;
      }
      if (hasSession) setInput(transcript);
    };
    recognition.onerror = () => setIsListening(false);
    recognitionRef.current = recognition;
    return () => {
      try {
        recognition.stop();
      } catch {
        /* ignore */
      }
    };
  }, [hasSession]);

  useEffect(() => {
    if (!autoOpen && !webOpen) return undefined;
    const onDown = e => {
      if (autoRef.current && !autoRef.current.contains(e.target)) setAutoOpen(false);
      if (webRef.current && !webRef.current.contains(e.target)) setWebOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [autoOpen, webOpen]);

  const handleSend = async () => {
    if (!hasSession || !input.trim() || isSubmitting) return;
    const text = input.trim();
    setInput('');
    if (textareaRef.current) textareaRef.current.style.height = 'auto';
    await submitMessage(text);
  };

  const handleInput = () => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
  };

  const selectAgent = next => {
    updateAgent(next);
    setSessionAgent(next);
    setChatmessages([]);
    setSessionID(null);
    setAutoOpen(false);
    navigate('/chat');
  };

  const displayAgent = selectedAgent?.label ? selectedAgent : sessionAgent;

  return (
    <>
      <div
        className="relative w-full"
        onMouseEnter={() => {
          if (!hasSession) setShowHint(true);
        }}
        onMouseLeave={() => setShowHint(false)}>
        {showHint && !hasSession && (
          <div className="pointer-events-none absolute -top-10 left-1/2 z-10 -translate-x-1/2 whitespace-nowrap rounded-lg border border-shell-card-border bg-white px-3 py-1.5 text-xs text-muted shadow-lg">
            Click <span className="text-primary font-medium">New Chat</span> to start a session
          </div>
        )}

        <ComposerRow>
          <ComposerField>
            <textarea
              ref={textareaRef}
              value={hasSession ? input : ''}
              onChange={e => {
                if (!hasSession) return;
                setInput(e.target.value);
              }}
              onInput={handleInput}
              onKeyDown={e => {
                if (!hasSession) {
                  e.preventDefault();
                  return;
                }
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              disabled={!hasSession}
              readOnly={!hasSession}
              placeholder={hasSession ? 'Send a message...' : 'Click New Chat to start a session...'}
              rows={1}
              title={!hasSession ? 'Click New Chat to start a session' : undefined}
              aria-label="Chat message"
              className="composer-native"
            />

            <ComposerTools>
              <div className="agent-chip-wrap" ref={autoRef}>
                <div className="agent-chip-group">
                  <button
                    type="button"
                    className="agent-avatar"
                    disabled={isDisabled}
                    title={displayAgent?.label || 'Select agent'}
                    onClick={() => {
                      if (isDisabled) return;
                      setAutoOpen(prev => !prev);
                      setWebOpen(false);
                    }}>
                    <Lightbulb />
                  </button>
                  <button
                    type="button"
                    className={`agent-avatar google ${webSearch ? '' : 'muted'}`}
                    disabled={isDisabled}
                    title={webSearch ? 'Web search on' : 'Web search off'}
                    onClick={() => {
                      if (isDisabled) return;
                      updateWebSearch();
                    }}>
                    G
                  </button>
                </div>

                {autoOpen && (
                  <div className="agent-menu">
                    <span className="menu-label">Agents</span>
                    {agentsQuery.isPending && (
                      <div style={{ padding: 12 }}>
                        <Loaders buttonLoader />
                      </div>
                    )}
                    {!agentsQuery.isPending &&
                      agentOptions.map(elem => {
                        const active = String(displayAgent?.value) === String(elem.id);
                        const icon = resolveAgentIcon(elem);
                        return (
                          <button
                            type="button"
                            key={elem.id}
                            className={`agent-option ${active ? 'active' : ''}`}
                            onClick={() =>
                              selectAgent({
                                label: elem.agent_name,
                                value: elem.id,
                                icon: elem.icon,
                                mode: 'agent',
                              })
                            }>
                            <span className="opt-icon">
                              {icon ? <img src={icon} alt="" /> : <Lightbulb size={14} />}
                            </span>
                            <span className="opt-label">{elem.agent_name}</span>
                          </button>
                        );
                      })}

                    {teamData.length > 0 && (
                      <>
                        <span className="menu-label">Teams</span>
                        {teamData.map(elem => {
                          const active =
                            displayAgent?.mode === 'team' &&
                            String(displayAgent?.value) === String(elem.team_id);
                          return (
                            <button
                              type="button"
                              key={elem.team_id}
                              className={`agent-option ${active ? 'active' : ''}`}
                              onClick={() =>
                                selectAgent({
                                  label: elem.team_name,
                                  value: elem.team_id,
                                  icon: elem.icon,
                                  mode: 'team',
                                })
                              }>
                              <span className="opt-icon">
                                {elem.icon ? <img src={elem.icon} alt="" /> : <Lightbulb size={14} />}
                              </span>
                              <span className="opt-label">{elem.team_name}</span>
                            </button>
                          );
                        })}
                      </>
                    )}
                  </div>
                )}
              </div>

              <div ref={webRef} style={{ position: 'relative' }}>
                <button
                  type="button"
                  className={`tool-btn ${webSearch ? 'active' : ''}`}
                  disabled={isDisabled}
                  title={!hasSession ? 'Click New Chat to start a session' : 'Web search'}
                  onClick={() => {
                    if (isDisabled) return;
                    setWebOpen(prev => !prev);
                    setAutoOpen(false);
                  }}>
                  <Globe className="w-4 h-4" />
                </button>
                {webOpen && (
                  <div className="tool-menu">
                    <div className="head">
                      <Globe className="w-4 h-4" />
                      <span>Web Search</span>
                      <div
                        className={`btn-check ${webSearch ? 'checked' : ''}`}
                        onClick={() => updateWebSearch()}
                      />
                    </div>
                    <p>Search the web for relevant info</p>
                  </div>
                )}
              </div>

              <button
                type="button"
                className="tool-btn"
                disabled={isDisabled}
                title={!hasSession ? 'Click New Chat to start a session' : 'Attach'}
                onClick={() => {
                  if (isDisabled) return;
                  setAttachOpen(true);
                }}>
                <Paperclip className="w-4 h-4" />
              </button>

              <button
                type="button"
                className={`tool-btn ${isListening ? 'active' : ''}`}
                disabled={isDisabled}
                title={!hasSession ? 'Click New Chat to start a session' : 'Voice'}
                onClick={() => {
                  if (isDisabled || !recognitionRef.current) return;
                  if (isListening) {
                    recognitionRef.current.stop();
                    setIsListening(false);
                  } else {
                    recognitionRef.current.start();
                    setIsListening(true);
                  }
                }}>
                <Mic className="w-4 h-4" />
              </button>
            </ComposerTools>
          </ComposerField>

          <ComposerSend
            type="button"
            onClick={handleSend}
            disabled={isDisabled || !input.trim()}
            title={!hasSession ? 'Click New Chat to start a session' : 'Send'}
            aria-label="Send message">
            {loading ? <Loaders buttonLoader /> : <Send className="w-4 h-4" />}
          </ComposerSend>
        </ComposerRow>
      </div>

      {attachOpen && <UploadFileModal onClose={() => setAttachOpen(false)} />}
    </>
  );
}
